import { themeQuartz } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { Plus, Trash2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import images from "../../../assets/images";
import Modal from "../../../components/modal/Modal";
import FileUpload from "../../../components/FileUpload/FileUpload";
import { useDropzone } from "react-dropzone";
import { toast } from "react-toastify";
import { useS3Upload, S3TempUrl } from "../../../utils/s3Client";
import DocumentViewer from "../../../components/DocumentViewer/DocumentViewer";
import PdfViewer from "../../../components/pdf/PdfViewer";
import { useHazardousWasteStore } from "../../../store/hazardousMaterialStore";
import { HazardousWasteLogType, Attachment } from "../../../types/api";
import {
  useUnitsofMeasureStore,
  useLocationStore,
} from "../../../store/masterDataStore";

const gridTheme = themeQuartz.withParams({
  backgroundColor: "#fff",
  foregroundColor: "#2a2a2a",
  headerTextColor: "#2a2a2a",
  headerBackgroundColor: "#E9F5FF",
  oddRowBackgroundColor: "rgba(0, 0, 0, 0.03)",
  headerColumnResizeHandleColor: "rgb(126, 46, 132)",
  fontSize: "0.75rem",
  headerFontWeight: "bold",
});

const WASTE_CATEGORIES = [
  "Infectious",
  "Sharps",
  "Pathological and anatomical",
  "Pharmaceutical",
  "Chemicals/heavy metals/pressurized containers",
  "Genotoxic/cytotoxic",
  "Radioactive material",
];

const emptyForm: HazardousWasteLogType = {
  wasteCategory: "",
  wasteType: "",
  generatedFromLocation: "",
  approxQuantity: "",
  quantityUnit: "",
  wasteDate: new Date().toISOString().slice(0, 10),
  notes: "",
  checklistFormat: "",
  attachments: [],
};

const HazardousWaste: React.FC = () => {
  const [rowData, setRowData] = useState<HazardousWasteLogType[]>([]);
  const [isModalOpen, setModalOpen] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [formData, setFormData] = useState<HazardousWasteLogType>(emptyForm);
  const [editId, setEditId] = useState<string | null>(null);

  const [files, setFiles] = useState<File[] | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(
    []
  );
  const [maxFileSize] = useState<number>(10);
  const [uploadErrorMessage, setUploadErrorMessage] = useState("");

  const [fileurl, setFileUrl] = useState({
    url: "",
    name: "",
    type: "",
  });
  const [errorMessage, setErrorMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isDocumentViewerOpen, setIsDocumentViewerOpen] = useState(false);

  const { getsignedurl } = S3TempUrl();
  const { uploadFileToS3, progress: uploadProgress, uploading } = useS3Upload();

  const {
    wasteLogs,
    loading,
    fetchAllWasteLogs,
    createWasteLog,
    updateWasteLog,
    deleteWasteLog,
  } = useHazardousWasteStore();

  const { unitsofMeasure, unitsofMeasureMap, fetchAllUnitsofMeasure } =
    useUnitsofMeasureStore();

  const { allLocations, allLocationsMap, fetchAllLocations } =
    useLocationStore();

  useEffect(() => {
    fetchAllWasteLogs();
    if (!unitsofMeasure.length) fetchAllUnitsofMeasure();
    if (!allLocations.length) fetchAllLocations();
  }, [fetchAllWasteLogs, fetchAllUnitsofMeasure, fetchAllLocations]);

  useEffect(() => {
    setRowData(wasteLogs ?? []);
  }, [wasteLogs]);

  const getFileExtension = (filename: string): string =>
    filename.split(".").pop()?.toLowerCase() || "";

  const canPreviewFile = (filename: string): boolean => {
    const ext = getFileExtension(filename);
    return [
      "jpg",
      "jpeg",
      "png",
      "pdf",
      "txt",
      "doc",
      "docx",
      "ppt",
      "pptx",
    ].includes(ext);
  };

  const handlePreviewFile = async (attachment: Attachment) => {
    const filename =
      attachment.filename ||
      attachment.name ||
      attachment.originalName ||
      attachment.url?.split("/").pop() ||
      "";

    if (!filename || !canPreviewFile(filename)) {
      toast.info(
        "Preview not available for this file type. Use download instead."
      );
      return;
    }

    try {
      setIsLoading(true);
      const s3Key = attachment.url?.split("/").pop();
      if (!s3Key) {
        throw new Error("Invalid file URL");
      }

      const signedUrl = await getsignedurl(s3Key, getFileExtension(filename));
      setFileUrl({
        url: signedUrl,
        name: filename,
        type: getFileExtension(filename),
      });
      setIsDocumentViewerOpen(true);
    } catch (err: any) {
      console.error("Error previewing file:", err);
      setErrorMessage("File not found or failed to load");
      toast.error("Failed to preview file");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseDocumentViewer = () => {
    setIsDocumentViewerOpen(false);
    setFileUrl({ url: "", name: "", type: "" });
    setErrorMessage("");
  };

  const handleRemoveExistingAttachment = (index: number) => {
    if (isEditMode) {
      setExistingAttachments((prev) => {
        const updated = [...prev];
        updated.splice(index, 1);
        return updated;
      });
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFilesChange = (newFiles: File[] | null) => {
    setFiles(newFiles);
  };

  const handleFileChange = (acceptedFiles: File[]) => {
    const maxTotalSize = maxFileSize * 1024 * 1024;
    const existingFiles = files || [];
    const existingTotalSize = existingFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );

    const newFilesTotalSize = acceptedFiles.reduce(
      (sum, file) => sum + file.size,
      0
    );
    const totalSize = existingTotalSize + newFilesTotalSize;

    if (totalSize > maxTotalSize) {
      toast.error(`Total file size exceeds ${maxFileSize}MB limit.`);
      return;
    }

    const updatedFiles = [...existingFiles, ...acceptedFiles];
    handleFilesChange(updatedFiles);
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop: handleFileChange,
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc", ".docx"],
      "text/plain": [".txt"],
      "image/jpeg": [".jpg", ".jpeg"],
      "image/png": [".png"],
    },
    disabled: isEditMode || submitting || uploading,
    multiple: true,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const missingFields: string[] = [];
    if (!formData.wasteCategory) missingFields.push("Waste Category");
    if (!formData.generatedFromLocation)
      missingFields.push("Generated From - Location");
    if (!formData.approxQuantity) missingFields.push("Approx Quantity");
    if (!formData.quantityUnit) missingFields.push("Unit of Quantity");
    if (!formData.wasteDate) missingFields.push("Date");

    if (missingFields.length > 0) {
      toast.error(
        `Please fill in all required fields: ${missingFields.join(", ")}`
      );
      return;
    }

    if (files && files.length > 0) {
      const maxTotalSize = maxFileSize * 1024 * 1024;
      const totalSize = files.reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxTotalSize) {
        toast.error(
          `Total file size exceeds ${maxFileSize}MB limit. Please remove some files.`
        );
        setUploadErrorMessage(
          "Total file size exceeds 10MB limit. Please remove some files."
        );
        return;
      }
    }

    setSubmitting(true);
    setUploadErrorMessage("");

    let uploadedFileObjects: Attachment[] = [];

    try {
      if (files && files.length > 0) {
        for (const file of files) {
          const s3Url = await uploadFileToS3(file);
          if (s3Url) {
            uploadedFileObjects.push({
              url: s3Url,
              name: file.name,
              uploadedAt: new Date().toISOString(),
            });
          } else {
            throw new Error(`Failed to upload file: ${file.name}`);
          }
        }
      }
    } catch (error) {
      setUploadErrorMessage(`File upload failed: ${(error as Error).message}`);
      setSubmitting(false);
      return;
    }

    try {
      const existingAttachmentObjects = existingAttachments.map((a) => ({
        url: a.url || (typeof a === "string" ? a : ""),
        name:
          a.filename ||
          a.name ||
          a.originalName ||
          a.url?.split("/").pop() ||
          "Unknown file",
        uploadedAt: a.uploadedAt || new Date().toISOString(),
      }));

      const payload: HazardousWasteLogType = {
        ...formData,
        attachments: [...existingAttachmentObjects, ...uploadedFileObjects],
      };

      let success;
      if (isEditMode && editId) {
        success = await updateWasteLog(payload, editId);
      } else {
        success = await createWasteLog(payload);
      }

      if (success) {
        toast.success(
          isEditMode
            ? "Hazardous waste entry updated successfully"
            : "Hazardous waste entry created successfully"
        );
        handleModalClose();
      }
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        (isEditMode
          ? "Failed to update hazardous waste entry"
          : "Failed to create hazardous waste entry");
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (data: HazardousWasteLogType) => {
    setIsEditMode(true);

    let attachmentsArray: Attachment[] = [];
    if (data.attachments && Array.isArray(data.attachments)) {
      attachmentsArray = data.attachments.map((attachment: any) => {
        if (typeof attachment === "string") {
          return {
            url: attachment,
            filename: attachment.split("/").pop() || "Unknown file",
            uploadedAt: new Date().toISOString(),
          };
        }
        return {
          url: attachment.url || attachment,
          filename:
            attachment.filename ||
            attachment.name ||
            attachment.originalName ||
            attachment.url?.split("/").pop() ||
            "Unknown file",
          uploadedAt: attachment.uploadedAt || new Date().toISOString(),
        };
      });
    }

    setFormData({
      ...data,
      wasteDate: data.wasteDate
        ? new Date(data.wasteDate).toISOString().slice(0, 10)
        : "",
    });

    setExistingAttachments(attachmentsArray);
    setEditId(data._id || "");
    setModalOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (
      window.confirm(
        "Are you sure you want to delete this hazardous waste entry?"
      )
    ) {
      await deleteWasteLog(id);
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setFormData({
      ...emptyForm,
      wasteDate: new Date().toISOString().slice(0, 10),
    });
    setEditId(null);
    setIsEditMode(false);
    setFiles(null);
    setExistingAttachments([]);
  };

  const isDisabled = submitting || uploading;

  const columnDefs = useMemo<any[]>(
    () => [
      {
        field: "wasteDate",
        headerName: "Date",
        minWidth: 120,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          params.data.wasteDate
            ? new Date(params.data.wasteDate).toLocaleDateString("en-GB")
            : "",
      },
      {
        field: "wasteCategory",
        headerName: "Category",
        minWidth: 160,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "wasteType",
        headerName: "Type / Description",
        minWidth: 180,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "generatedFromLocation",
        headerName: "Generated From (Location)",
        minWidth: 200,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          allLocationsMap.get(params.data.generatedFromLocation) ||
          params.data.generatedFromLocation ||
          "N/A",
      },
      {
        field: "approxQuantity",
        headerName: "Qty",
        minWidth: 80,
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellStyle: { textAlign: "left", paddingLeft: 10 },
      },
      {
        field: "quantityUnit",
        headerName: "Unit",
        minWidth: 80,
        headerStyle: { backgroundColor: "#e9f5ff" },
        valueGetter: (params: any) =>
          unitsofMeasureMap.get(params.data.quantityUnit) ||
          params.data.quantityUnit ||
          "N/A",
      },
      {
        field: "notes",
        headerName: "Notes / Checklist Summary",
        minWidth: 220,
        headerStyle: { backgroundColor: "#e9f5ff" },
      },
      {
        field: "action",
        headerName: "Action",
        minWidth: 90,
        maxWidth: 110,
        cellStyle: { padding: "10px 15px 0 0", textAlign: "left" },
        headerStyle: { backgroundColor: "#e9f5ff" },
        cellRenderer: (params: any) => (
          <div className="flex gap-x-3 pt-2">
            <button
              onClick={() => handleEdit(params.data)}
              className="p-1 hover:bg-gray-100 rounded"
              title="Edit"
            >
              <img
                className="iconTd w-5 h-5"
                src={images.editIcon}
                alt="edit"
              />
            </button>
            <button
              onClick={() => handleDelete(params.data._id)}
              className="p-1 hover:bg-red-100 rounded"
              title="Delete"
            >
              <Trash2 className="text-red-500" size={23} />
            </button>
          </div>
        ),
      },
    ],
    [allLocationsMap, unitsofMeasureMap]
  );

  const defaultColDef = useMemo<any>(() => ({ editable: false }), []);
  const theme = useMemo<any>(() => gridTheme, []);

  return (
    <>
      {(uploading || submitting) && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full text-center">
            <div className="flex justify-center mb-4">
              <svg
                className="animate-spin h-8 w-8 text-teal-600"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
            </div>
            <h3 className="text-lg font-medium mb-2">
              {uploading ? "Uploading files..." : "Submitting..."}
            </h3>
            {uploading && (
              <p className="text-sm text-gray-600">
                Progress: {uploadProgress}%
              </p>
            )}
            {uploadErrorMessage && (
              <p className="text-xs text-red-500 mt-2">{uploadErrorMessage}</p>
            )}
          </div>
        </div>
      )}

      <div className="px-2 py-4 mt-14 min-h-screen">
        <div className="flex justify-between items-center mb-4">
          <h2 className="ml-2">Hazardous Waste - Daily Tracking</h2>
          <button
            className="flex items-center justify-center font-semibold gap-2 bg-[#0fa9a6] text-white border border-[#0fa9a6] px-4 py-2 rounded-full hover:bg-teal-600"
            onClick={() => {
              setModalOpen(true);
              setIsEditMode(false);
              setFormData({
                ...emptyForm,
                wasteDate: new Date().toISOString().slice(0, 10),
              });
              setExistingAttachments([]);
              setFiles(null);
            }}
          >
            <Plus size={20} /> Add Daily Waste Entry
          </button>
        </div>

        <p className="text-xs text-gray-500 mb-3 ml-2">
          Track daily hazardous waste generated by category (infectious, sharps,
          pharmaceutical, etc.), location, and quantity. You can attach your own
          checklist/format or uploaded forms for each day.
        </p>

        <div className="tableBlock w-full">
          <div className="card">
            {rowData?.length > 0 ? (
              <div className="ag-theme-alpine w-full min-h-[400px] rounded-lg shadow-lg">
                <AgGridReact
                  rowData={rowData}
                  columnDefs={columnDefs}
                  theme={theme}
                  headerHeight={65}
                  rowHeight={50}
                  loading={loading}
                  defaultColDef={defaultColDef}
                  domLayout="autoHeight"
                  pagination
                  paginationPageSize={5}
                />
              </div>
            ) : (
              <div className="flex flex-col justify-center items-center h-64">
                <p className="text-gray-500 text-lg mb-4">
                  No hazardous waste entries found
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        closeButton={false}
        modalClass="bg-white w-full max-w-4xl rounded-xl shadow-lg relative max-h-[80vh] flex flex-col"
      >
        <div className="pt-6 px-6 flex-1 overflow-y-auto">
          <h2 className="text-xl font-semibold text-gray-800 border-b pb-2 mb-6">
            {isEditMode
              ? "Update Hazardous Waste Entry"
              : "Add Hazardous Waste Entry"}
          </h2>
          <form
            onSubmit={handleSubmit}
            id="hazardousWasteForm"
            className="flex flex-col gap-6 pb-10"
          >
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="wasteDate"
                  className="text-sm font-medium text-gray-700"
                >
                  Date<span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  id="wasteDate"
                  name="wasteDate"
                  value={formData.wasteDate}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  required
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="wasteCategory"
                  className="text-sm font-medium text-gray-700"
                >
                  Waste Category<span className="text-red-500">*</span>
                </label>
                <select
                  id="wasteCategory"
                  name="wasteCategory"
                  value={formData.wasteCategory}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Category</option>
                  {WASTE_CATEGORIES.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="wasteType"
                  className="text-sm font-medium text-gray-700"
                >
                  Waste Type / Description
                </label>
                <input
                  type="text"
                  id="wasteType"
                  name="wasteType"
                  value={formData.wasteType}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="e.g., contaminated dressings, used syringes"
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="generatedFromLocation"
                  className="text-sm font-medium text-gray-700"
                >
                  Generated From - Location
                  <span className="text-red-500">*</span>
                </label>
                <select
                  id="generatedFromLocation"
                  name="generatedFromLocation"
                  value={formData.generatedFromLocation}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Location</option>
                  {allLocations.map((loc) => (
                    <option key={loc._id as string} value={loc._id as string}>
                      {loc.room}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="approxQuantity"
                  className="text-sm font-medium text-gray-700"
                >
                  Approx Quantity<span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  id="approxQuantity"
                  name="approxQuantity"
                  value={formData.approxQuantity}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  placeholder="Enter quantity (e.g., 5, 10.5)"
                  required
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2">
                <label
                  htmlFor="quantityUnit"
                  className="text-sm font-medium text-gray-700"
                >
                  Unit of Quantity<span className="text-red-500">*</span>
                </label>
                <select
                  id="quantityUnit"
                  name="quantityUnit"
                  value={formData.quantityUnit}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none"
                  required
                  disabled={isDisabled}
                >
                  <option value="">Select Unit</option>
                  {unitsofMeasure?.map((u) => (
                    <option key={u._id} value={u._id}>
                      {u.unitSymbol}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex flex-col gap-2 col-span-2">
                <label
                  htmlFor="notes"
                  className="text-sm font-medium text-gray-700"
                >
                  Notes / Checklist Summary
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  placeholder="Summarize any checklist used, key observations, or special handling notes"
                  rows={2}
                  disabled={isDisabled}
                />
              </div>

              <div className="flex flex-col gap-2 col-span-2">
                <label
                  htmlFor="checklistFormat"
                  className="text-sm font-medium text-gray-700"
                >
                  Custom Checklist / Format Description
                </label>
                <textarea
                  id="checklistFormat"
                  name="checklistFormat"
                  value={formData.checklistFormat}
                  onChange={handleChange}
                  className="w-full p-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#375998] focus:outline-none resize-y"
                  placeholder="Describe your custom checklist/format if not attached as a file"
                  rows={2}
                  disabled={isDisabled}
                />
              </div>
            </div>

            <FileUpload
              mode={isEditMode ? "update" : "create"}
              files={files}
              onFilesChange={handleFilesChange}
              existingAttachments={existingAttachments}
              onRemoveExistingAttachment={handleRemoveExistingAttachment}
              onPreviewFile={handlePreviewFile}
              uploading={uploading}
              uploadProgress={uploadProgress}
              disabled={isDisabled}
              maxFileSizeMB={maxFileSize}
              getRootProps={getRootProps}
              getInputProps={getInputProps}
            />

            <p className="text-xs text-gray-500">
              You can upload your own daily waste checklist, format, or forms
              from your computer, including photos of bins, storage areas, or
              transport containers.
            </p>

            <div className="h-10" />
          </form>
        </div>
        <div className="flex justify-end gap-3 p-3 bg-[#5A7192] w-full rounded-b-lg sticky bottom-0">
          <button
            type="button"
            className="px-4 py-2 bg-[#8497B0] text-white rounded-full hover:bg-teal-600 transition"
            onClick={handleModalClose}
            disabled={isDisabled}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="hazardousWasteForm"
            className="px-4 py-2 bg-[#00A8A8] text-white rounded-full hover:bg-teal-600 transition"
            disabled={isDisabled}
          >
            {isDisabled ? "Submitting..." : isEditMode ? "Update" : "Add"}
          </button>
        </div>
      </Modal>

      {isDocumentViewerOpen && (
        <div className="fixed inset-0 z-[10001] flex items-center justify-center bg-black bg-opacity-45">
          <div className="relative bg-white rounded-lg shadow-xl w-[90vw] h-[90vh] flex flex-col">
            <div className="absolute right-4 top-4 z-10">
              <button
                onClick={handleCloseDocumentViewer}
                className="bg-red-500 hover:bg-red-600 text-white p-2 rounded-full shadow-lg transition-colors duration-200"
                aria-label="Close document viewer"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex-1 p-4 overflow-auto">
              {errorMessage && (
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
                  {errorMessage}
                </div>
              )}
              {isLoading ? (
                <div className="flex justify-center items-center h-full">
                  <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                </div>
              ) : fileurl.url ? (
                fileurl.type === "pdf" ? (
                  <div className="w-full h-full">
                    <PdfViewer fileUrl={fileurl.url} />
                  </div>
                ) : (
                  <div className="w-full h-full">
                    <DocumentViewer file={fileurl.url} name={fileurl.name} />
                  </div>
                )
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p>No document to display</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HazardousWaste;
